/**
 * Rótulos de status partilhados + rótulo condicional "Pendente do Responsável".
 *
 * Predicado canónico: `status?.toUpperCase() === 'APROVADO' && Boolean(subcategory) && subcategory !== 'compras'`.
 * Aplicar o override SÓ quando subcategory está explicitamente setado para um
 * valor não-purchase (ex.: 'manutencao'); null/undefined/'' representam
 * requisições aprovadas em que a triagem ainda não classificou o fluxo — esses
 * mantêm o rótulo literal 'Aprovado'. Normalização de case é obrigatória:
 * registos legados lowercase do backend (ex.: 'aprovado') entram no mesmo ramo.
 */

/** Rótulo exibido quando manutenção aprovada aguarda execução pelo responsável */
export const PENDENTE_RESPONSAVEL_LABEL = 'Pendente do Responsável'

const STATUS_LABELS: Record<string, string> = {
  AGUARDANDO_TRIAGEM: 'Aguardando Triagem',
  PENDENTE: 'Pendente',
  APROVADO: 'Aprovado',
  EXECUTADO: 'Executado',
  REJEITADO: 'Rejeitado',
  CONCLUIDO: 'Concluído',
  ARQUIVADO: 'Arquivado',
}

/**
 * Rótulo de status com o tratamento condicional de APROVADO.
 * Manutenção aprovada com subcategory explicitamente 'manutencao' (ou outro
 * valor não-purchase) vira "Pendente do Responsável"; compras e subcategory
 * ausente/null/'' mantêm "Aprovado" — alinhado com o backend
 * (services/reportGenerator.js formatStatus) e com o resto das áreas
 * client-facing, para evitar divergência entre lista, formulário, history e
 * relatório.
 */
export function getStatusLabel(
  status?: string | null,
  subcategory?: 'manutencao' | 'compras' | null,
): string {
  if (
    status?.toUpperCase() === 'APROVADO' &&
    Boolean(subcategory) &&
    subcategory !== 'compras'
  ) {
    return PENDENTE_RESPONSAVEL_LABEL
  }
  const normalized = status?.toUpperCase()
  if (!normalized) return '—'
  return STATUS_LABELS[normalized] ?? status
}

/**
 * Prefixo da linha de data de uma requisição APROVADA.
 * Compras (subcategory==='compras') e subcategory ausente/null/'' mantêm
 * "Aprovado em:"; manutenção explicitamente setada vira
 * "Pendente do Responsável desde:". Alinhado com getStatusLabel.
 */
export function getApprovedDatePrefix(subcategory?: 'manutencao' | 'compras' | null): string {
  if (subcategory === 'compras' || !subcategory) return 'Aprovado em:'
  return `${PENDENTE_RESPONSAVEL_LABEL} desde:`
}

// Tokens sintéticos do filtro de status (issue #244). Vivem só no dropdown +
// este helper; nunca chegam ao backend porque são expandidos antes da chamada
// à API. Backend continua aceitando apenas o enum canónico AGUARDANDO_TRIAGEM..ARQUIVADO.
export const STATUS_FILTER_TOKEN_APROVADO_MANUTENCAO = 'APROVADO_MANUTENCAO' as const
export const STATUS_FILTER_TOKEN_APROVADO_COMPRAS = 'APROVADO_COMPRAS' as const

export interface ExpandedStatusFilter {
  status: string[] | undefined
  subcategory: ('manutencao' | 'compras')[] | undefined
}

/**
 * Expande os tokens sintéticos do filtro de status em pares reais
 * (status, subcategory) antes de chamar a API. O backend trata status +
 * subcategory como filtros independentes em sequência (ver
 * backend/src/services/maintenance.js:2635-2721), portanto a interseção
 * funciona automaticamente.
 *
 * Regras:
 * - APROVADO_MANUTENCAO → status=APROVADO + subcategory=manutencao
 * - APROVADO_COMPRAS    → status=APROVADO + subcategory=compras
 * - Outros status reais passam adiante intactos.
 * - subcategory já setado pelo utilizador (UI dropdown de subcategoria) é
 *   preservado quando nenhum token está presente.
 */
export function expandStatusFilter(filters: {
  status?: readonly string[] | null
  subcategory?: readonly ('manutencao' | 'compras')[] | null
}): ExpandedStatusFilter {
  const input = filters.status ?? []
  const real: string[] = []
  let manutWanted = false
  let comprasWanted = false
  for (const s of input) {
    if (s === STATUS_FILTER_TOKEN_APROVADO_MANUTENCAO) manutWanted = true
    else if (s === STATUS_FILTER_TOKEN_APROVADO_COMPRAS) comprasWanted = true
    else real.push(s)
  }

  let subcategoryOut: ('manutencao' | 'compras')[] | undefined
  if (manutWanted && !comprasWanted) subcategoryOut = ['manutencao']
  else if (!manutWanted && comprasWanted) subcategoryOut = ['compras']
  else if (manutWanted && comprasWanted) subcategoryOut = ['manutencao', 'compras']
  else subcategoryOut = filters.subcategory ? [...filters.subcategory] : undefined

  let statusOut: string[] | undefined
  if (manutWanted || comprasWanted) {
    statusOut = real.includes('APROVADO') ? real : [...real, 'APROVADO']
  } else {
    statusOut = real.length > 0 ? real : undefined
  }

  return { status: statusOut, subcategory: subcategoryOut }
}
